/**
 * Inline comment chips in Monaco (utils/commentChipsModel.js): a content widget at the end of each line with open
 * comment threads, "SC · 1 comment" in the colour of who started it; a click opens the comments panel on that thread.
 * Uses the threads useEditorCollab already loads; widgets follow the lines as the text changes.
 */
import { useEffect, useRef } from 'react';
import { commentChips } from '../utils/commentChipsModel';
import { inkFor } from '../utils/participantColors';

export default function useCommentChips({ editor, monaco, threads, onOpen }) {
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!editor || !monaco) { return undefined; }
    const model = editor.getModel?.();
    if (!model) { return undefined; }
    const widgets = [];
    const decorations = editor.createDecorationsCollection ? editor.createDecorationsCollection([]) : null;
    const chips = commentChips(threads, model.getLineCount());
    // A sticky decoration per chip keeps track of its line while people type above it.
    decorations?.set(chips.map((chip) => ({
      range: new monaco.Range(chip.line, 1, chip.line, 1),
      options: { stickiness: monaco.editor.TrackedRangeStickiness?.NeverGrowsWhenTypingAtEdges ?? 1 },
    })));
    chips.forEach((chip, index) => {
      const node = document.createElement('button');
      node.type = 'button';
      node.className = 'collab-comment-chip';
      node.textContent = chip.text;
      node.title = chip.label;
      node.setAttribute('aria-label', chip.label);
      node.dataset.testid = 'comment-chip';
      node.style.setProperty('--presence-ink', inkFor(chip.slot));
      node.addEventListener('mousedown', (event) => { event.preventDefault(); event.stopPropagation(); });
      node.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); onOpenRef.current?.(chip.threadId, chip.line); });
      const lineOf = () => {
        const range = decorations?.getRange?.(index);
        return range ? range.startLineNumber : chip.line;
      };
      const widget = {
        allowEditorOverflow: false,
        getId: () => `collab.commentChip.${chip.threadId}.${index}`,
        getDomNode: () => node,
        getPosition: () => {
          const line = Math.min(lineOf(), model.getLineCount());
          return { position: { lineNumber: line, column: model.getLineMaxColumn(line) }, preference: [monaco.editor.ContentWidgetPositionPreference.EXACT] };
        },
      };
      editor.addContentWidget(widget);
      widgets.push(widget);
    });
    // Typing moves the end of the line: place the chips again.
    const sub = widgets.length ? model.onDidChangeContent?.(() => widgets.forEach((w) => { try { editor.layoutContentWidget(w); } catch { /* disposed */ } })) : null;
    return () => {
      sub?.dispose?.();
      widgets.forEach((widget) => { try { editor.removeContentWidget(widget); } catch { /* disposed */ } });
      try { decorations?.clear(); } catch { /* disposed */ }
    };
  }, [editor, monaco, threads]);
}
